import React, { ComponentType, FocusEventHandler } from "react"
import { RequiredSymbol, FavSelector } from "./various"
import { InputError } from "./inputError"
import { InLocalStorage } from "../InLocalStorage"
import { DropdownList } from "./dropdownList";
import { isEqual } from "lodash";
import { StringsHelper } from "../stringsHelper";
import { ComboBoxWidget } from "./comboBox";
export interface GroupByObject<T, TGroup> {
	selector: (item: T) => TGroup,
	component?: (props: { item: TGroup }) => JSX.Element | string
}

export function Dropdown<T>(): { new(): DropdownSelect<T> } {
	return DropdownSelect as any;
}
export function Grouped<T, TGroup>(): { new(): DropdownSelect<T, TGroup> } {
	return DropdownSelect as any;
}

interface IDropdownProps<T, TGroup> {
	label?: string;
	data: Array<T>;
	onSelect: (value: T | null) => void;
	value: T | null;
	error?: string;
	itemComponent?: ComponentType<{ item: T, isDetailedView: boolean }>;
	placeholder?: string;
	readonly?: boolean;
	busy?: boolean;
	allowNull?: boolean;
	keepOpen?: boolean;
	groupBy?: GroupByObject<T, TGroup>;
	required?: boolean | undefined;
	title?: string;
	onBlur?: FocusEventHandler;
	onFocus?: FocusEventHandler;
	textField: (item: T) => string;
	remember?: {
		storageKey: string,
		keySelector: (item: T) => string;
	}
}

export function getComponent<T, TGroup>(itemComponent: ComponentType<{ item: T, isDetailedView: boolean }> | undefined, item: T | null, isDetailedView: boolean, textField: (item: T) => string) {
	if (item == null) return null;
	if (itemComponent == null) return <>{textField(item)}</>;
	let Comp = itemComponent;
	return <Comp item={item} isDetailedView={isDetailedView} />
}

interface IDropdownState<T> {
	isOpen: boolean | null;
}

export class DropdownSelect<T, TGroup = void> extends React.Component<IDropdownProps<T, TGroup>, IDropdownState<T>>
{
	private storage = this.props.remember ? new InLocalStorage<string>("Dropdown_" + this.props.remember.storageKey) : null;
	private a = React.createRef<HTMLAnchorElement>();
	private filter: string | null = null;
	constructor(props: IDropdownProps<T, TGroup>) {
		super(props);
		this.state = { isOpen: null };
	}

	private onSelect = (v: T | null) => {
		if (v == null) {
			if (this.props.allowNull) {
				this.props.onSelect(null);
			}
		} else {
			const index = this.props.data.indexOf(v);
			const newState = { ...this.state };
			if (index > -1) {
				if (this.props.value !== v) {
					this.props.onSelect(v);
				}
			}
			newState.isOpen = this.props.keepOpen || null;
			if (!isEqual(newState, this.state))
				this.setState(newState);
		}
	};

	private selectIfNeeded() {
		const { allowNull, data, value } = this.props;
		if (value == null) {
			if (!allowNull && data.length === 1) {
				this.onSelect(data[0]);
			}
			else if (this.storage) {
				const key = this.storage.get();
				if (key) {
					var item = data.find(x => this.props.remember!.keySelector(x) == key);
					if (item) {
						this.onSelect(item);
					}
				}
			}
		}
	}

	componentDidMount() {
		this.selectIfNeeded();
	}

	handleKeyDown = (e: React.KeyboardEvent) => {
		e.metaKey
		switch (e.key) {
			case "ArrowUp":
				this.dropDownMenu.current?.moveUp();
				break;
			case "ArrowDown":
				this.dropDownMenu.current?.moveDown();
				break;
			case "Enter":
				e.preventDefault();
				this.dropDownMenu.current?.select();
				break;
			case "Tab":
				this.dropDownMenu.current?.select();
				break;
			case "Backspace":
				this.filter = null;
				break;
			case "Escape":
				if (this.state.isOpen) {
					this.setState({ isOpen: false })
					this.dropDownMenu.current?.resetSelection();
				}
				break;
			default:
				if (e.key.length === 1 && this.props.data.length > 0 && e.key.charCodeAt(0) >= 32 && e.key.charCodeAt(0) <= 122) {
					if (this.filter == null) {
						this.filter = e.key;
						setTimeout(() => {
							this.filter = null
						}, 2000);
					}
					else {
						this.filter += e.key;
					}
					const { index, item } = StringsHelper.BestMatch(this.props.data, this.props.textField, this.filter)
					if (item != null)
						this.dropDownMenu.current?.selectIndex(index);
				}
				break;
		}
	}

	componentDidUpdate() {
		this.selectIfNeeded();
	}

	public toggle = () => {
		this.setState({ isOpen: !this.state.isOpen }, () => { if (this.state.isOpen) this.a.current?.focus() });
	}

	private onFocus = (event: React.FocusEvent) => {
		this.props.onFocus && this.props.onFocus(event);
		if (!this.state.isOpen)
			this.setState({ isOpen: true });
	}

	private onBlur = (event: React.FocusEvent) => {
		this.props.onBlur && this.props.onBlur(event);
		if (this.state.isOpen) {
			setTimeout(() => this.setState({ isOpen: false }), 300);
		}
	}

	private Component = (props: { item: T, isDetailedView: boolean }) => {
		const { itemComponent: ItemComponent, remember } = this.props;
		const showFavs = this.storage != null;
		const key = props.item != null && remember ? remember.keySelector(props.item) : null;
		const isFavourite = this.storage?.get() == key;
		return <div style={{
			display: "flex",
			flexDirection: "row",
			alignItems: "center"
		}}>
			{
				<div style={{ flex: 1 }}>
					{
						ItemComponent
							? <ItemComponent
								item={props.item}
								isDetailedView={props.isDetailedView}
							/>
							: this.props.textField(props.item)
					}
				</div>
			}
			{showFavs ? <div>
				<FavSelector
					selected={isFavourite}
					onChange={selected => this.storage!.set(selected ? key ?? null : null)}
				/>
			</div> : null}
		</div>
	}

	private onClick = () => {
		if (this.a.current === document.activeElement && !this.state.isOpen)
			this.setState({ isOpen: true });
	}
	private dropDownMenu = React.createRef<DropdownList<T, TGroup>>();
	render() {
		const { label, title, required, readonly, allowNull, busy, placeholder, value, data } = this.props;
		const { isOpen } = this.state;
		return (
			<div onKeyDown={this.handleKeyDown} title={title} style={{ position: "relative" }}>
				{label && <label className="form-label" onClick={e => this.a.current!.focus()}>{label}</label>} {required ? <RequiredSymbol /> : undefined}
				<div className={`form-control bncombo`}>
					<a
						className="d-flex align-items-center"
						ref={this.a}
						style={{ width: "100%", border: "none", color: "#67757c", height: "100%" }}
						tabIndex={0}
						onFocus={this.onFocus}
						onBlur={this.onBlur}
						onClick={this.onClick}>
						{getComponent(this.props.itemComponent, value, false, this.props.textField) || <span style={{ color: "#a6b7bf" }}>{placeholder}</span>}
					</a>
					<ComboBoxWidget
						busy={busy}
						selectedItem={value}
						allowNull={allowNull}
						onSelect={this.onSelect}
						onToggle={this.toggle}
						readonly={readonly}
					/>
				</div>
				<DropdownList
					ref={this.dropDownMenu}
					isOpen={isOpen || false}
					items={data}
					onSelect={this.onSelect}
					itemComponent={x => getComponent(this.Component, x, true, this.props.textField)}
				/>
				<InputError error={this.props.error} />
			</div >
		);
	}
}


interface ResponsiveDropdownProps<T> extends IDropdownProps<T, {}> {
	idSelector: (item: T) => string;
}

export class ResponsiveDropdown<T> extends React.PureComponent<ResponsiveDropdownProps<T>> {
	private dl: any;

	public toggle() {
		this.dl.toggle();
	}

	private onSelectEvent = (e: React.FormEvent<HTMLSelectElement>) => {
		var item = (this.props.data || []).find(x => this.props.idSelector(x) === (e.target as any).value);
		this.props.onSelect(item || null);
	}
	render() {
		let CB = Dropdown<T>() as any;
		const { idSelector, ...props } = this.props;
		return (
			<div className="w-100">
				<div className="d-none d-sm-block">
					<CB ref={(cb: any) => this.dl = cb!} {...props} />
				</div>
				<div className="d-block d-sm-none">
					<select className="form-control text-uppercase default-border"
						style={{ fontSize: "0.9em", height: 38 }}
						onChange={this.onSelectEvent}
						value={this.props.value && this.props.idSelector(this.props.value) || ""}
						disabled={this.props.readonly}>
						<option value="" disabled={Boolean(this.props.value)}>{this.props.placeholder}</option>
						{this.props.data.map(x => <option value={this.props.idSelector(x)} key={this.props.idSelector(x)}>{this.props.textField(x)}</option>)}
					</select>
				</div>
			</div>
		);
	}
}
